/**
 * @fileoverview Desktop sidebar collapse/expand toggle button.
 * @description Sits at the bottom of DashboardLayout's sidebar, alongside the app version, below
 * `NavMenu`. Icon-only in both states (no text label ever) — a bare chevron that flips direction —
 * matching the reference design's compact footer row. Split out of DashboardLayout itself purely
 * to keep that shell's own render function short.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

interface SidebarCollapseToggleProps {
  collapsed: boolean
  onToggle: () => void
}

/**
 * @component
 * @param {SidebarCollapseToggleProps} props - The sidebar's current collapsed state and toggle
 * handler.
 * @param {boolean} props.collapsed - Whether the sidebar is currently in its icon-only width.
 * @param {() => void} props.onToggle - Called when the button is clicked.
 */
function SidebarCollapseToggle({ collapsed, onToggle }: SidebarCollapseToggleProps) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={t(collapsed ? 'layout.expandNav' : 'layout.collapseNav')}
      className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-accent-text hover:bg-accent-hover"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`shrink-0 transition-transform ${collapsed ? 'rotate-180' : ''}`}
        aria-hidden="true"
      >
        <path d="M11 5l-6.5 7 6.5 7" />
        <path d="M18.5 5L12 12l6.5 7" />
      </svg>
    </button>
  )
}

export default SidebarCollapseToggle
